import { LIMITS } from '@konvoi/shared';
import type { PositionSample, StatusMessageKind } from '@konvoi/shared';
import { openDB, type IDBPDatabase } from 'idb';

/**
 * Der Puffer für das Funkloch.
 *
 * Auf einer Ausfahrt über Nebenstrecken ist der Empfang streckenweise weg.
 * Positionen, die nicht hinausgehen, werden hier abgelegt und nachgesendet,
 * sobald die Verbindung zurück ist. Ohne das entstünde in der gemeinsamen Spur
 * genau dort ein Loch, wo es am ehesten gebraucht wird — im Funkloch verliert
 * man den Anschluss.
 *
 * Der Puffer ist begrenzt. Läuft er über, werden die ältesten Meldungen
 * verworfen: Wer eine Stunde ohne Empfang war, dem hilft die Position von vor
 * einer Stunde nichts mehr.
 *
 * Jeder Eintrag trägt seine Ausfahrt bei sich. Der Puffer überdauert das
 * Schließen der App, und was von gestern übrig ist, gehört nicht in die
 * Ausfahrt von heute: Der Server verwirft Messungen, die älter sind als der
 * bereits erreichte Stand, sie wären also nicht zustellbar. Angezeigt würden
 * sie trotzdem — als warteten sie auf eine Gelegenheit, die nie kommt.
 */

const DB_NAME = 'konvoi';
const STORE = 'pending-positions';
const STORE_STATUS = 'pending-status';
const INDEX_TOUR = 'tourId';
const DB_VERSION = 3;

/** Ein Eintrag im Puffer: die Messung samt der Ausfahrt, zu der sie gehört. */
interface PendingPosition {
  readonly recordedAt: number;
  readonly tourId: string;
  readonly sample: PositionSample;
}

/**
 * Eine Statusmeldung, die auf Zustellung wartet.
 *
 * ## Warum die anders behandelt wird als eine Position
 *
 * Eine Position lässt sich ersetzen — die nächste kommt in einer Sekunde, und
 * ein Loch in der Spur ist ärgerlich, mehr nicht. Eine **Panne** nicht: Sie
 * entsteht einmal, auf einen Fingertipp hin, und wenn sie verschwindet, merkt
 * es niemand. Bis zum 14.09.2026 geschah genau das — `send` gab `false`
 * zurück, und `sendStatus` las die Antwort nicht.
 *
 * **Ohne Verfallszeit**, abgestimmt am 14.09.2026: Was abgesetzt wurde, kommt
 * an. Eine Panne von vor zwanzig Minuten besteht meistens noch; und für die
 * Meldungen, die sich überholen, richtet eine späte Zustellung keinen Schaden
 * an. Der Zeitpunkt reist deshalb mit — sie erscheint mit der Uhrzeit des
 * Tippens, nicht des Eingangs.
 *
 * **Und ohne Obergrenze.** Anders als bei den Positionen füllt hier kein Takt
 * den Speicher, sondern ein Daumen: Was sich ansammelt, hat jemand einzeln
 * angetippt. Begrenzt wird der Puffer über die Ausfahrt — `dropForeign` räumt
 * beim Wechsel alles weg, was nicht dazugehört.
 */
export interface PendingStatus {
  /** Wann der Fahrer getippt hat. Zugleich der Schlüssel. */
  readonly recordedAt: number;
  readonly tourId: string;
  readonly kind: StatusMessageKind;
  readonly lat: number | null;
  readonly lon: number | null;
}

let verbindung: Promise<IDBPDatabase> | null = null;

function database(): Promise<IDBPDatabase> {
  // Gemerkt wird das Versprechen, nicht erst sein Ergebnis: Sonst öffneten
  // zwei gleichzeitige Zugriffe zwei Verbindungen, und deren Schreib- und
  // Lesevorgänge stünden in keiner verlässlichen Reihenfolge zueinander.
  verbindung ??= openDB(DB_NAME, DB_VERSION, {
    upgrade(datenbank, alteFassung) {
      /*
       * Nur Fassung 1 wird weggeworfen — sie kannte keine Zugehörigkeit zur
       * Ausfahrt, und solche Einträge lassen sich keiner mehr zuordnen.
       *
       * Die Bedingung stand bis zum 14.09.2026 auf `alteFassung > 0` und traf
       * damit **jede** künftige Aufrüstung. Beim Sprung auf Fassung 3 hätte
       * sie die wartenden Positionen eines Geräts weggeworfen, das gerade im
       * Funkloch steht und das neue Bündel geladen hat — der Puffer ist für
       * genau diesen Augenblick gebaut.
       */
      if (alteFassung > 0 && alteFassung < 2 && datenbank.objectStoreNames.contains(STORE)) {
        datenbank.deleteObjectStore(STORE);
      }

      // Der Schlüssel ist der Zeitpunkt der Messung: Damit liegen die
      // Einträge von sich aus in der richtigen Reihenfolge, und dieselbe
      // Messung kann nicht zweimal im Puffer landen.
      if (!datenbank.objectStoreNames.contains(STORE)) {
        const speicher = datenbank.createObjectStore(STORE, { keyPath: 'recordedAt' });
        speicher.createIndex(INDEX_TOUR, 'tourId');
      }

      // Fassung 3: die Statusmeldungen. Derselbe Schlüssel aus demselben Grund.
      if (!datenbank.objectStoreNames.contains(STORE_STATUS)) {
        const meldungen = datenbank.createObjectStore(STORE_STATUS, { keyPath: 'recordedAt' });
        meldungen.createIndex(INDEX_TOUR, 'tourId');
      }
    },
  });
  return verbindung;
}

export async function bufferPosition(tourId: string, sample: PositionSample): Promise<void> {
  await bufferPositions(tourId, [sample]);
}

/**
 * Mehrere Messungen auf einmal ablegen, in einer Transaktion.
 *
 * Gebraucht für das, was die Verbindung zurückgibt, nachdem sie sich als tot
 * herausgestellt hat — bis zu einer Minute Messungen, oder eine ganze
 * Nachsendung, die gleich wieder abriss (A-137). Liegt dieselbe Messung schon
 * im Puffer, bleibt es bei einem Eintrag: Der Schlüssel ist ihr Zeitpunkt.
 */
export async function bufferPositions(
  tourId: string,
  samples: readonly PositionSample[],
): Promise<void> {
  if (samples.length === 0) {
    return;
  }
  const datenbank = await database();
  const ablage = datenbank.transaction(STORE, 'readwrite');
  await Promise.all(
    samples.map((sample) => {
      const eintrag: PendingPosition = { recordedAt: sample.recordedAt, tourId, sample };
      return ablage.store.put(eintrag);
    }),
  );
  await ablage.done;

  const anzahl = await datenbank.count(STORE);
  if (anzahl > LIMITS.offlineBufferMaxPositions) {
    const zuViele = anzahl - LIMITS.offlineBufferMaxPositions;
    const älteste = await datenbank.getAllKeys(STORE, null, zuViele);
    const transaktion = datenbank.transaction(STORE, 'readwrite');
    await Promise.all(älteste.map((schlüssel) => transaktion.store.delete(schlüssel)));
    await transaktion.done;
  }
}

/**
 * Die ältesten wartenden Meldungen dieser Ausfahrt, höchstens so viele wie in
 * eine Sendung passen.
 *
 * Der Index liefert sie nach Schlüssel geordnet — und der Schlüssel ist der
 * Zeitpunkt der Messung. Die Reihenfolge stimmt also von selbst.
 */
export async function peekBuffered(
  tourId: string,
  limit: number = LIMITS.positionsPerBatch,
): Promise<PositionSample[]> {
  const datenbank = await database();
  const einträge = (await datenbank.getAllFromIndex(
    STORE,
    INDEX_TOUR,
    tourId,
    limit,
  )) as PendingPosition[];
  return einträge.map((eintrag) => eintrag.sample);
}

/** Entfernt die Meldungen, deren Ankunft bestätigt ist. */
export async function dropBuffered(samples: readonly PositionSample[]): Promise<void> {
  const datenbank = await database();
  const transaktion = datenbank.transaction(STORE, 'readwrite');
  await Promise.all(samples.map((sample) => transaktion.store.delete(sample.recordedAt)));
  await transaktion.done;
}

export async function bufferedCount(tourId: string): Promise<number> {
  const datenbank = await database();
  return datenbank.countFromIndex(STORE, INDEX_TOUR, tourId);
}

/**
 * Wirft weg, was zu einer anderen Ausfahrt gehört. Liefert die Anzahl.
 *
 * Läuft beim Beitritt. Ein Neuladen mitten in der Fahrt trifft nur fremde
 * Einträge und lässt die eigenen unangetastet.
 */
export async function dropForeign(tourId: string): Promise<number> {
  const datenbank = await database();
  let entfernt = 0;

  for (const speicher of [STORE, STORE_STATUS]) {
    const alle = (await datenbank.getAll(speicher)) as { recordedAt: number; tourId: string }[];
    const fremd = alle.filter((eintrag) => eintrag.tourId !== tourId);
    if (fremd.length === 0) {
      continue;
    }
    const transaktion = datenbank.transaction(speicher, 'readwrite');
    await Promise.all(fremd.map((eintrag) => transaktion.store.delete(eintrag.recordedAt)));
    await transaktion.done;
    entfernt += fremd.length;
  }

  return entfernt;
}

/**
 * Eine Statusmeldung ablegen, die nicht hinausging.
 *
 * Der Schlüssel ist der Zeitpunkt des Tippens. Zwei Meldungen in derselben
 * Millisekunde kann ein Daumen nicht erzeugen; wäre es doch so, bliebe die
 * zweite — und ein doppelter Tipp auf dieselbe Fläche ist ohnehin einer zu viel.
 */
export async function bufferStatus(eintrag: PendingStatus): Promise<void> {
  const datenbank = await database();
  await datenbank.put(STORE_STATUS, eintrag);
}

/** Alle wartenden Meldungen dieser Ausfahrt, älteste zuerst. */
export async function peekBufferedStatus(tourId: string): Promise<PendingStatus[]> {
  const datenbank = await database();
  const alle = (await datenbank.getAllFromIndex(
    STORE_STATUS,
    INDEX_TOUR,
    tourId,
  )) as PendingStatus[];
  return alle.sort((a, b) => a.recordedAt - b.recordedAt);
}

/** Zugestellt — aus dem Puffer nehmen. */
export async function dropBufferedStatus(eintraege: readonly PendingStatus[]): Promise<void> {
  if (eintraege.length === 0) {
    return;
  }
  const datenbank = await database();
  const transaktion = datenbank.transaction(STORE_STATUS, 'readwrite');
  await Promise.all(eintraege.map((e) => transaktion.store.delete(e.recordedAt)));
  await transaktion.done;
}

/**
 * Wie viele Meldungen warten.
 *
 * Der Fahrer muss das sehen. Wer auf „Panne" tippt und nichts geschieht,
 * glaubt sonst, es sei niemand unterwegs zu ihm.
 */
export async function bufferedStatusCount(tourId: string): Promise<number> {
  const datenbank = await database();
  return datenbank.countFromIndex(STORE_STATUS, INDEX_TOUR, tourId);
}
